import { createApiRef, FetchApi, OAuthApi } from '@backstage/core-plugin-api';

export const armApiRef = createApiRef<ArmApi>({
  id: 'plugin.kaap.armapi',
});

export type AdGroup = { id: string; name: string };

export type GetGroupsParams = {
  ids?: string[];
  match?: string;
  isMember?: boolean;
};

export interface ArmApi {
  getGroup(groupId: string): Promise<AdGroup>;
  getGroups(params: GetGroupsParams): Promise<AdGroup[]>;
}

const GRAPH_GROUP_SCOPE = 'https://graph.microsoft.com/Group.Read.All';

// Azure AD groups that are not meaningful tenant security groups and should be
// filtered out of pickers and membership checks.
const WELL_KNOWN_GROUPS = [
  'dcedc4ca-ef7d-4e38-8f98-713505aa017b',
  'b2f412ba-da2f-4adf-a171-8439a18822e7',
  '5543ef50-dfb8-4209-9675-30633be0e91c',
  '62376fb8-8ecd-4b5d-9283-f8c90855ce12',
  '97ed1212-89a3-45b8-8ab0-795197fb3bb2',
  'cf7e0dca-ea6e-4851-a081-a0c9a4e82b2e',
  'f5e93fdd-1c6f-475d-bea2-2c24ed90a8a1',
];

const groupFilter = (group: AdGroup): boolean =>
  !WELL_KNOWN_GROUPS.includes(group.id) &&
  !group.name?.toUpperCase().startsWith('RG_AZ_PIM_') &&
  !group.name?.toUpperCase().startsWith('PG_CONCUR_SSO') &&
  !group.name?.toUpperCase().startsWith('RG_APP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_APP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_LOC_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_TT_') &&
  !group.name?.toUpperCase().startsWith('RG_VPN_') &&
  !group.name?.toUpperCase().startsWith('SPO_LOCATION_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_SLACK_USER_GROUP_') &&
  !group.name?.toUpperCase().startsWith('RG_AZ_CLOUDSPEND_READERS');

export type ArmApiClientOptions = {
  fetchApi: FetchApi;
  microsoftAuth: OAuthApi;
};

/**
 * Resolves Azure AD security-group display names via the Microsoft Graph API,
 * using an access token obtained through the Backstage Microsoft auth session.
 */
export class ArmApiClient implements ArmApi {
  private readonly fetchApi: FetchApi;
  private readonly microsoftAuth: OAuthApi;

  constructor(options: ArmApiClientOptions) {
    this.fetchApi = options.fetchApi;
    this.microsoftAuth = options.microsoftAuth;
  }

  async getGroup(groupId: string): Promise<AdGroup> {
    const token = await this.microsoftAuth.getAccessToken(GRAPH_GROUP_SCOPE);
    const url = `https://graph.microsoft.com/v1.0/groups/${groupId}?$select=id,displayName`;
    const response = await this.fetchApi.fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        ConsistencyLevel: 'eventual',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to resolve AD group ${groupId}: ${response.status}`,
      );
    }

    const group = await response.json();
    return { id: group.id, name: group.displayName };
  }

  async getGroups(params: GetGroupsParams): Promise<AdGroup[]> {
    const token = await this.microsoftAuth.getAccessToken(GRAPH_GROUP_SCOPE);
    const headers = {
      Authorization: `Bearer ${token}`,
      ConsistencyLevel: 'eventual',
    };

    let url: string;
    let init: RequestInit = { headers };

    if (params.ids && params.ids.length > 0) {
      url = 'https://graph.microsoft.com/v1.0/directoryObjects/getByIds';
      init = {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: params.ids, types: ['group'] }),
      };
    } else {
      const path = params.isMember
        ? 'me/memberOf/microsoft.graph.group'
        : 'groups';
      let filter = 'mailEnabled eq false and securityEnabled eq true';
      if (params.match) {
        filter += ` and startswith(displayName, '${params.match}')`;
      }
      const query = new URLSearchParams({
        $count: 'true',
        $filter: filter,
        $select: 'id,displayName',
      });
      url = `https://graph.microsoft.com/v1.0/${path}?${query.toString()}`;
    }

    const response = await this.fetchApi.fetch(url, init);
    if (!response.ok) {
      throw new Error(`Failed to resolve AD groups: ${response.status}`);
    }

    const body = await response.json();
    const groups: AdGroup[] = (body.value ?? []).map((group: any) => ({
      id: group.id,
      name: group.displayName,
    }));
    return groups.filter(groupFilter);
  }
}
